import React, { useRef, useState } from 'react';
import Popover from '@/components/ui/Popover/Popover';
import styles from './TextToSpeechButton.module.scss';

interface SpeedSelectMenuProps {
  selectedSpeed?: string,
  setSelectedSpeed: Function,
  draggableToolbarClass?: string,
}

interface TtsSpeedButtonProps {
  buttonRef: React.RefObject<HTMLButtonElement>,
  isSelected: boolean,
  speed: string,
  onSelect: (speed: string) => void,
}

const TtsSpeedButton = ({ buttonRef, isSelected, speed, onSelect }: TtsSpeedButtonProps) => (
  <button
    ref={buttonRef}
    aria-label={`Select ${speed.toLowerCase()} speed`}
    className={`dropdown-item ${styles.ttsSpeedButton}`}
    type="button"
    onClick={() => onSelect(speed.toLowerCase())}
  >
    <span
      className={styles.ttsSpeedSelectText}
      style={{ visibility: isSelected ? 'visible' : 'hidden' }}
    >
      <i className="fa fa-check" />
    </span>
    <span>{`${speed} Speed`}</span>
  </button>
);

const SpeedSelectMenu = ({ selectedSpeed, setSelectedSpeed, draggableToolbarClass = '' }: SpeedSelectMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const slowButtonRef = useRef<HTMLButtonElement>(null);
  const normalButtonRef = useRef<HTMLButtonElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();

      if (document.activeElement === slowButtonRef.current) {
        normalButtonRef.current?.focus();
      } else {
        slowButtonRef.current?.focus();
      }
    } else if (e.key === ' ') {
      e.preventDefault();
      (document.activeElement as HTMLElement)?.click();
    }
  };

  const handleSpeedSelect = (speed: string) => {
    setSelectedSpeed(speed);
    setIsOpen(false);
    triggerButtonRef.current?.focus();
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          ref={triggerButtonRef}
          aria-expanded={isOpen}
          aria-label="Open speed selection menu"
          className={`${styles.ttsActionDropdown} tw-pl-2 tw-pr-2 ${draggableToolbarClass}`}
          type="button"
          onKeyDown={(e) => {
            if (e.key === 'Tab' && isOpen) {
              setIsOpen(false);
            } else if (e.key === ' ') {
              e.preventDefault();
              setIsOpen(!isOpen);
            }
          }}
        >
          <i className="fa fa-caret-down" />
        </button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className="tw-py-2"
        side="bottom"
        sideOffset={0}
        width="unset"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          triggerButtonRef.current?.focus();
        }}
        onKeyDownCapture={handleContentKeyDown}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          slowButtonRef.current?.focus();
        }}
      >
        <TtsSpeedButton
          buttonRef={slowButtonRef}
          isSelected={selectedSpeed === 'slow'}
          speed="Slow"
          onSelect={handleSpeedSelect}
        />
        <TtsSpeedButton
          buttonRef={normalButtonRef}
          isSelected={selectedSpeed === 'normal'}
          speed="Normal"
          onSelect={handleSpeedSelect}
        />
      </Popover.Content>
    </Popover>
  );
};

export default SpeedSelectMenu;
